import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef } from 'react';
import { X, FileUp, AlertCircle } from 'lucide-react';
import { knowledgeService } from '../../services/knowledge.service';
import { useAuth } from '../../contexts/AuthContext';
import { parseFile, getFileInputAccept } from '../../utils/fileParser';
export const CreateArticleModal = ({ isOpen, onClose, organizationId, categories, onArticleCreated }) => {
    const { user } = useAuth();
    const fileInputRef = useRef(null);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('');
    const [content, setContent] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        setError('');
        setUploadProgress(10);
        setIsLoading(true);
        try {
            // Parse the file
            setUploadProgress(50);
            const parsed = await parseFile(file);
            setUploadProgress(80);
            // Set the parsed content and title
            setTitle(parsed.title);
            setContent(parsed.content);
            setUploadProgress(100);
            // Reset progress after a short delay
            setTimeout(() => setUploadProgress(0), 1000);
        }
        catch (err) {
            console.error('File upload error:', err);
            setError(err.message || 'Failed to upload file. Please try again.');
            setUploadProgress(0);
        }
        finally {
            setIsLoading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!title.trim()) {
            setError('Title is required');
            return;
        }
        if (!category && !newCategory) {
            setError('Please select or create a category');
            return;
        }
        if (!user?.id) {
            setError('User not authenticated');
            return;
        }
        if (!organizationId) {
            setError('Organization not found');
            return;
        }
        setIsLoading(true);
        try {
            const finalCategory = newCategory || category;
            await knowledgeService.createArticle({
                title: title.trim(),
                content: content.trim(),
                category: finalCategory,
                organization_id: organizationId,
                author_id: user.id
            });
            // Reset form
            setTitle('');
            setCategory('');
            setContent('');
            setNewCategory('');
            onArticleCreated();
            onClose();
        }
        catch (err) {
            console.error('Failed to create article:', err);
            setError('Failed to create article. Please try again.');
        }
        finally {
            setIsLoading(false);
        }
    };
    if (!isOpen)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 z-40", onClick: onClose }), _jsxs("div", { className: "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl rounded-lg shadow-xl z-50 border", style: {
                    backgroundColor: 'var(--bg-base)',
                    borderColor: 'var(--border-color)'
                }, children: [_jsxs("div", { className: "flex items-center justify-between p-6 border-b", style: {
                            borderColor: 'var(--border-color)',
                            backgroundColor: 'var(--bg-elevated)'
                        }, children: [_jsx("h2", { className: "text-xl font-bold", style: { color: 'var(--text-primary)' }, children: "Create New Article" }), _jsx("button", { onClick: onClose, className: "p-2 rounded-lg hover:bg-opacity-50 transition-colors", style: {
                                    backgroundColor: 'var(--bg-base)',
                                    color: 'var(--text-secondary)'
                                }, children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsxs("form", { onSubmit: handleSubmit, className: "p-6 space-y-4", children: [error && (_jsxs("div", { className: "p-3 rounded-lg text-sm flex items-start gap-2", style: {
                                    backgroundColor: '#FEE2E2',
                                    color: '#DC2626',
                                    borderLeft: '4px solid #DC2626'
                                }, children: [_jsx(AlertCircle, { className: "w-4 h-4 flex-shrink-0 mt-0.5" }), _jsx("span", { children: error })] })), uploadProgress > 0 && uploadProgress < 100 && (_jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex items-center justify-between text-sm", children: [_jsx("span", { style: { color: 'var(--text-secondary)' }, children: "Uploading..." }), _jsxs("span", { style: { color: 'var(--text-muted)' }, children: [uploadProgress, "%"] })] }), _jsx("div", { className: "h-2 rounded-full overflow-hidden", style: { backgroundColor: 'var(--bg-elevated)' }, children: _jsx("div", { className: "h-full transition-all duration-300", style: {
                                                width: `${uploadProgress}%`,
                                                backgroundColor: '#A855F7'
                                            } }) })] })), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-3", style: { color: 'var(--text-secondary)' }, children: "Upload Article (Optional)" }), _jsxs("div", { className: "relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:bg-opacity-50", style: {
                                            borderColor: 'var(--border-color)',
                                            backgroundColor: 'var(--bg-elevated)'
                                        }, onClick: () => fileInputRef.current?.click(), children: [_jsx("input", { ref: fileInputRef, type: "file", accept: getFileInputAccept(), onChange: handleFileUpload, className: "hidden", disabled: isLoading }), _jsx(FileUp, { className: "w-8 h-8 mx-auto mb-2", style: { color: '#A855F7' } }), _jsx("p", { className: "font-medium", style: { color: 'var(--text-primary)' }, children: "Drop your file here or click to browse" }), _jsx("p", { className: "text-xs mt-1", style: { color: 'var(--text-muted)' }, children: "Supports .docx, .md, .txt files" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Title" }), _jsx("input", { type: "text", value: title, onChange: (e) => setTitle(e.target.value), placeholder: "Enter article title", className: "w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-opacity-50", style: {
                                            backgroundColor: 'var(--bg-elevated)',
                                            borderColor: 'var(--border-color)',
                                            color: 'var(--text-primary)',
                                            focusRingColor: '#A855F7'
                                        }, disabled: isLoading })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Existing Category" }), _jsxs("select", { value: category, onChange: (e) => {
                                                    setCategory(e.target.value);
                                                    if (e.target.value)
                                                        setNewCategory('');
                                                }, className: "w-full px-3 py-2 rounded-lg border outline-none focus:ring-2", style: {
                                                    backgroundColor: 'var(--bg-elevated)',
                                                    borderColor: 'var(--border-color)',
                                                    color: 'var(--text-primary)'
                                                }, disabled: isLoading, children: [_jsx("option", { value: "", children: "Select a category..." }), categories.map((cat) => (_jsx("option", { value: cat, children: cat }, cat)))] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Or Create New" }), _jsx("input", { type: "text", value: newCategory, onChange: (e) => {
                                                    setNewCategory(e.target.value);
                                                    if (e.target.value)
                                                        setCategory('');
                                                }, placeholder: "Enter new category", className: "w-full px-3 py-2 rounded-lg border outline-none focus:ring-2", style: {
                                                    backgroundColor: 'var(--bg-elevated)',
                                                    borderColor: 'var(--border-color)',
                                                    color: 'var(--text-primary)'
                                                }, disabled: isLoading })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Content" }), _jsx("textarea", { value: content, onChange: (e) => setContent(e.target.value), placeholder: "Enter article content (supports markdown)", rows: 8, className: "w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 resize-none", style: {
                                            backgroundColor: 'var(--bg-elevated)',
                                            borderColor: 'var(--border-color)',
                                            color: 'var(--text-primary)'
                                        }, disabled: isLoading })] }), _jsxs("div", { className: "flex justify-end gap-3 pt-4", children: [_jsx("button", { type: "button", onClick: onClose, className: "px-4 py-2 rounded-lg border transition-colors", style: {
                                            borderColor: 'var(--border-color)',
                                            color: 'var(--text-secondary)',
                                            backgroundColor: 'var(--bg-base)'
                                        }, disabled: isLoading, children: "Cancel" }), _jsx("button", { type: "submit", className: "px-4 py-2 rounded-lg text-white transition-colors", style: { backgroundColor: '#A855F7' }, disabled: isLoading, children: isLoading ? 'Creating...' : 'Create Article' })] })] })] })] }));
};
